import { Component, ElementRef, HostListener, OnInit, ViewChild, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';

@Component({
  selector: 'app-blog-obfuscation',
  imports: [CommonModule, RouterModule],
  standalone: true,
  templateUrl: './blog-obfuscation.component.html',
  styleUrl: './blog-obfuscation.component.scss'
})
export class BlogObfuscationComponent implements OnInit, OnDestroy {
  private jsonLdScript: any;
  constructor(private router: Router, private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }

  @ViewChild('tableresponsiveImage') image!: ElementRef<HTMLImageElement>;

  originalWidth = 2880;
  originalHeight = 768;

  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route: { data: any; }) => route.data)
    )
      .subscribe((event: any) => {
        if (this.router.url === '/blogs/data-obfuscation') {
          this.updateDescription(event.description, event.keywords, event.title);
        }
      });
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  injectSchemaMarkup(): void {
    const obfuscationSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/data-obfuscation#webpage",
          "url": "https://www.kalpitatechnologies.com/tech-blogs/data-obfuscation",
          "name": "Kalpita Data Obfuscation Tool for Secured Data",
          "description": "Complete guide to Kalpita's Data Obfuscation Tool covering what data obfuscation is, how the tool functions, and key benefits.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/data-obfuscation#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/", "name": "Home" } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/tech-blogs/blog", "name": "Blog Index" } },
            { "@type": "ListItem", "position": 3, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/tech-blogs/data-obfuscation", "name": "Data Obfuscation" } }
          ]
        },
        {
          "@type": "BlogPosting",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/data-obfuscation#blogposting",
          "headline": "Kalpita Data Obfuscation Tool for Secured Data",
          "articleBody": "In the modern era, everything has become digitalized and there's an increase in data risk. Data obfuscation, also known as data masking, is the process of transforming sensitive information into a non-sensitive form...",
          "author": { "@type": "Person", "name": "Grace Catharine" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "datePublished": "2024-02-28",
          "image": "https://www.kalpitatechnologies.com/assets/resources/blog/Obfuscation/Obfuscation-banner.webp"
        },
        {
          "@type": "HowTo",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/data-obfuscation#howto",
          "name": "How to Use Kalpita Data Obfuscation Tool",
          "step": [
            { "@type": "HowToStep", "position": 1, "name": "Set Up Project Master", "text": "Set up a new project with a server connection string for database connection." },
            { "@type": "HowToStep", "position": 2, "name": "Configure Column Mapping", "text": "Select project and table, fetch columns, and choose obfuscation functions." },
            { "@type": "HowToStep", "position": 3, "name": "Admin Setup", "text": "Deploy obfuscation functions and add keywords." },
            { "@type": "HowToStep", "position": 4, "name": "Execute Obfuscation", "text": "Run the obfuscation process on mapped tables and columns." }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/data-obfuscation#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is data obfuscation?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Data obfuscation (data masking) is the process of transforming sensitive information into a non-sensitive or anonymous form to safeguard it from unauthorized access."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(obfuscationSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  ngAfterViewInit(): void {
    this.resizeImageMap();
    if (this.image?.nativeElement) {
      this.image.nativeElement.onload = () => this.resizeImageMap();
    }
  }

  @HostListener('window:resize')
  resizeImageMap() {
    if (!this.image?.nativeElement) return;

    const img = this.image.nativeElement;
    const widthRatio = img.clientWidth / this.originalWidth;
    const heightRatio = img.clientHeight / this.originalHeight;

    document.querySelectorAll('map[name="table-image-map"] area').forEach((area: any) => {
      let coords = area.getAttribute('data-original-coords') || area.getAttribute('coords');
      if (!coords) return;

      area.setAttribute('data-original-coords', coords);
      const scaledCoords = coords.split(',').map((coord: any, index: number) =>
        Math.round(Number(coord) * (index % 2 === 0 ? widthRatio : heightRatio))
      );
      area.setAttribute('coords', scaledCoords.join(','));
    });
  }

  updateDescription(desc: string, keywords: string, title: string) {
    // this.seoService.updateDescription(desc);
    // this.seoService.updateMetaKeywords(keywords);
    // this.seoService.updateTitle(title);
  }

  scrollToSection(sectionId: string): void {
    const targetElement = document.getElementById(sectionId);
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      const offset = 5 * 16;
      const scrollPosition = rect.top + window.pageYOffset - offset;
      window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    }
  }
}
